import AxiosAPI from '@qualitas/libs';
import { AxiosResponse } from 'axios';

export interface FundraisingCandidate {
    id: string;
    name: string;
    vehicle: string;
    commitment: number;
    displayLabel: string;
}

export interface CompanyCandidate {
    id: string;
    name: string;
}

export interface UnlinkedContract {
    contractId: number;
    name: string;
    vehicle: string;
    commitment: number;
    idPersonType?: number;
    companyName?: string;
    idVehicle?: number;
}

export interface CreateFundraisingProcessPayload {
    idVehicle: number;
    projectedCommitment: number;
    estimatedCloseDate: string;
    idCompany?: number;
    companyName?: string;
}

type FundraisingProcessResponse = {
    id?: string | number;
    entryId?: string | number;
    fundraisingProcessId?: string | number;
    name?: string;
    investor?: string;
    investorName?: string;
    investorLegalEntity?: string;
    vehicle?: string;
    product?: string;
    productVehicle?: string;
    commitment?: number | string;
    projectedCommitment?: number | string;
    currentCommitmentsCcy?: number | string;
};

type CompanyResponse = {
    id?: string | number;
    entryId?: string | number;
    companyId?: string | number;
    name?: string;
    companyName?: string;
};

type CreateFundraisingProcessResponse = {
    fundraiseProcessId?: string | number;
    FundraiseProcessId?: string | number;
};

type FundraisingLinkContractResponse = {
    contractId: number;
    name: string;
    vehicle: string;
    commitment: number;
    idPersonType: number;
    companyName?: string;
    idVehicle: number;
};

const unwrapResponse = <T>(response: AxiosResponse): T[] => {
    const payload = response.data?.data ?? response.data;
    return Array.isArray(payload) ? payload : payload?.items ?? payload?.rows ?? [];
};

const toCandidate = (process: FundraisingProcessResponse): FundraisingCandidate => {
    const id = process.id ?? process.entryId ?? process.fundraisingProcessId;
    const name = process.name ?? process.investorName ?? process.investor ?? process.investorLegalEntity ?? '';
    const vehicle = process.vehicle ?? process.product ?? process.productVehicle ?? '';
    const commitment = Number(process.commitment ?? process.projectedCommitment ?? process.currentCommitmentsCcy ?? 0);

    return {
        id: String(id ?? ''),
        name,
        vehicle,
        commitment: Number.isFinite(commitment) ? commitment : 0,
        displayLabel: `${id ?? ''} · ${name}`
    };
};

const toCompanyCandidate = (company: CompanyResponse): CompanyCandidate => ({
    id: String(company.id ?? company.entryId ?? company.companyId ?? ''),
    name: company.name ?? company.companyName ?? ''
});

const toUnlinkedContract = (contract: FundraisingLinkContractResponse): UnlinkedContract => ({
    contractId: contract.contractId,
    name: contract.name,
    vehicle: contract.vehicle,
    commitment: contract.commitment,
    idPersonType: contract.idPersonType,
    companyName: contract.companyName,
    idVehicle: contract.idVehicle
});

export const FundraisingLinkAPI = (baseURL: string | undefined, clientId: string) => {
    const dealcloudAPI = AxiosAPI({
        baseURL,
        clientId,
        isApp: true,
        hasUnderMaintenance: false
    });

    return {
    getUnlinkedContracts: (): Promise<UnlinkedContract[]> =>
        dealcloudAPI.get('/bookbuilding-api/Contract/GetContractsWithoutFundraisingProcess')
            .then((response: AxiosResponse) => unwrapResponse<FundraisingLinkContractResponse>(response).map(toUnlinkedContract)),
    getFundraisingCandidates: (): Promise<FundraisingCandidate[]> =>
        dealcloudAPI.get('/dealcloud-api/Dealcloud/fundraise-processes/active')
            .then((response: AxiosResponse) => unwrapResponse<FundraisingProcessResponse>(response).map(toCandidate)),
    getCompanyCandidates: (): Promise<CompanyCandidate[]> =>
        dealcloudAPI.get('/dealcloud-api/Dealcloud/fundraise-processes/companies')
            .then((response: AxiosResponse) => unwrapResponse<CompanyResponse>(response)
                .map(toCompanyCandidate)
                .filter(company => Boolean(company.id) && Boolean(company.name))),
    saveFundraisingProccessId: (contractId: number, fundraisingId: string): Promise<void> => {
        const fundraisingProccessId = fundraisingId.trim();
        if (!fundraisingProccessId) {
            return Promise.reject(new Error('El ID de Fundraising seleccionado no es válido.'));
        }

        return dealcloudAPI.patch(`/bookbuilding-api/Contract/${contractId}/FundraisingProccessId`, {
            fundraisingProccessId
        }).then(() => undefined);
    },
    createFundraisingProcess: (payload: CreateFundraisingProcessPayload): Promise<string> =>
        dealcloudAPI.post('/dealcloud-api/Dealcloud/fundraise-processes', payload)
            .then((response: AxiosResponse) => {
                const result = (response.data?.data ?? response.data) as CreateFundraisingProcessResponse;
                const fundraisingProcessId = result?.fundraiseProcessId ?? result?.FundraiseProcessId;

                if (fundraisingProcessId === undefined || fundraisingProcessId === null) {
                    return Promise.reject(new Error('El servicio no devolvió el ID del Fundraise process creado.'));
                }

                return String(fundraisingProcessId);
            })
    };
};
